<template>
  <div class="article-table-root">
    <div
      class="d-flex flex-wrap ma-5"
      style="column-gap: 20px; row-gap: 20px; max-width: calc(50em + 20px);"
    >
      <button
        v-for="route in articleCreationRoutes"
        :key="route.name"
        type="button"
        class="home-start-card"
        :data-test="route.name === 'AnalyzeArticle' ? 'home-analyze-card' : 'home-create-card'"
        @click="createArticle(route.name)"
        @keydown="onStartCardKey($event, route.name)"
      >
        <v-card
          :class="{'pie-card' : !$vuetify.theme.dark}"
          :color="hasCreditForRoute(route.name) ? 'accent' : 'warning'"
          dark
          hover
          width="100%"
        >
          <v-card-title class="d-flex flex-row flex-nowrap align-start">
            <v-icon
              class="mr-2"
              large
            >
              add
            </v-icon>
            <span class="font-weight-bold">
              {{ route.text }}
              <br>
              <span
                class="caption"
                style="opacity: .9; font-size: .9rem !important;"
              >
                {{ route.description }}
              </span>
            </span>
          </v-card-title>
        </v-card>
      </button>
    </div>
    <v-card class="mb-8 articles-table-card">
      <v-data-table
        :loading="loading"
        :items="articles"
        :headers="headers"
        :server-items-length="serverItemsLength"
        :options.sync="options"
        :footer-props="{ disablePagination : loading,
                         disableItemsPerPage : loading,
                         'items-per-page-options': itemsPerPageOptions }"
        :disable-sort="loading"
      >
        <template #item.selectedKeyword="{ item }">
          <router-link
            :to="'/article/' + item.id"
            class="text-decoration-none"
          >
            {{ articleKeyword(item) }}
          </router-link>
        </template>
        <template #item.url="{ item, index }">
          <span v-if="item.url === ''">
            -
          </span>
          <span v-else>
            <a
              :href="item.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-decoration-none"
            >{{ item.url }}</a>
          </span>
          <v-tooltip
            v-if="['organicPosition', 'googleAIOPosition', 'chatGPTPosition']
              .some(field => !item[field] || !item[field].isProcessing)"
            top
          >
            <template #activator="{ on }">
              <v-btn
                icon
                plain
                v-on="on"
              >
                <v-icon @click="openUrlDialog(index)">
                  edit
                </v-icon>
              </v-btn>
            </template>
            {{ $t('articleTable.url.tooltip.' + (item.url === '' ? 'add' : 'edit')) }}
          </v-tooltip>
        </template>
        <template #item.created_at="{ item }">
          {{ item.created_at | date }}
        </template>
        <template
          v-for="field of Object.values(positionSources).map(s => s.field)"
          #[headerSlotName(field)]="{ header }"
        >
          {{ header.text }}
          <v-tooltip
            :key="field"
            top
          >
            <template #activator="{ on }">
              <v-icon
                small
                v-on="on"
              >
                help
              </v-icon>
            </template>
            {{ $t(`articleTable.${field}.tooltip`) }}
          </v-tooltip>
        </template>
        <template
          v-for="[sourceKey, config] of Object.entries(positionSources)"
          #[itemSlotName(config.field)]="{ item }"
        >
          <ReloadableChip
            v-if="item[config.field]"
            :key="sourceKey"
            :value="item[config.field]"
            :hint="$t('articleTable.reloadableChip.remeasure')"
            :color-fn="positionColor"
            @reload="remeasurePosition(item, sourceKey)"
          />
          <span
            v-else
            :key="'none-' + config.field"
          >
            -
          </span>
        </template>
        <template #item.seoScore="{ item }">
          <v-chip
            class="article-table__seo-chip"
            :class="'article-table__seo-chip--' + seoScoreColor(item.seoScore)"
            :color="seoScoreColor(item.seoScore)"
            :text-color="item.seoScore > 66 ? undefined : 'white'"
          >
            {{ item.seoScore + ' %' }}
          </v-chip>
        </template>
        <template #header.url="{ header }">
          {{ header.text }}
          <v-tooltip top>
            <template #activator="{ on }">
              <v-icon
                small
                v-on="on"
              >
                help
              </v-icon>
            </template>
            {{ $t('articleTable.url.header.tooltip') }}
          </v-tooltip>
        </template>
        <template #header.domainSeoScore="{ header }">
          {{ header.text }}
          <v-tooltip top>
            <template #activator="{ on }">
              <v-icon
                small
                v-on="on"
              >
                help
              </v-icon>
            </template>
            {{ $t('articleTable.domainSeoScore.header.tooltip') }}
          </v-tooltip>
        </template>
        <template #item.domainSeoScore="{ item }">
          <v-tooltip
            v-if="item.url !== ''"
            top
          >
            <template #activator="{ on }">
              <v-btn
                icon
                outlined
                elevation="0"
                v-on="on"
                @click="openDomainSeoScore(item)"
              >
                <v-icon>
                  open_in_new
                </v-icon>
              </v-btn>
            </template>
            {{ $t('articleTable.domainSeoScore.tooltip') }}
          </v-tooltip>
          <span v-else>
            -
          </span>
        </template>
        <template #item.actions="{ item }">
          <v-tooltip top>
            <template #activator="{ on }">
              <router-link :to="'/article/' + item.id">
                <v-btn
                  icon
                  outlined
                  elevation="0"
                  v-on="on"
                >
                  <v-icon>
                    {{ articleEditIcon(item) }}
                  </v-icon>
                </v-btn>
              </router-link>
            </template>
            {{ $t('steps.step') }}: {{ articleEditLabel(item) }}
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
    <UrlEditDialog
      v-model="urlDialog"
      :article="editingArticleIndex !== null ? articles[editingArticleIndex] : null"
      :loading="urlDialogLoading"
      :error="urlDialogError"
      @action="updateArticleUrl"
    />
  </div>
</template>

<script lang="ts">
    import Vue from "vue";
    import {Firestore} from "@/plugins/Firestore";
    import {Article, ArticlesPage, PositionSource, SearchPosition} from "@/model/Article";
    import UserPlanMixin from "@/mixins/UserPlanMixin";
    import {TranslateResult} from "vue-i18n";
    import {DataTableHeader} from "vuetify/types";
    import {DocumentData} from "firebase/firestore";
    import {Sort, SortString} from "@/model/Sort";
    import UrlEditDialog from "@/components/article/table/components/UrlEditDialog.vue";
    import ReloadableChip from "@/components/article/table/components/ReloadableChip.vue";
    import {ExternalLinks} from "@/config/ExternalLinks";
    import {Toast} from "@/model/Toast";
    import {articlePositionSources} from "@/utils/Article";
    import {articleAccessFromStore, runWhenPlanLoaded} from "@/service/articleLimitGate";

    interface ArticleCreationRoute {
        name: string;
        text: string;
        description: string;
    }

    export default Vue.extend({
        name: 'ArticleTable',
        components: {UrlEditDialog, ReloadableChip},
        mixins: [UserPlanMixin],
        data: () => ({
            loading: false,
            articles: [] as Article[],

            options: {
                itemsPerPage: 10,
                sortBy: ['created_at'],
                sortDesc: [true],
                multiSort: false,
                mustSort: false
            },
            serverItemsLength: 0,
            initialLoad: true,
            articlesPage: null as ArticlesPage | null,
            itemsPerPageOptions: [10, 25, 50, 100],

            urlDialog: false,
            urlDialogLoading: false,
            urlDialogError: null as string | null,
            editingArticleIndex: null as number | null,

            positionSources: articlePositionSources,
        }),
        computed: {
            headers: function (): DataTableHeader[] {
                return [
                    {
                        text: this.$t('articleTable.selectedKeyword') as string,
                        value: 'selectedKeyword',
                        sortable: false,
                    },
                    {
                        text: this.$t('articleTable.url.header.text') as string,
                        value: 'url',
                        sortable: false,
                    },
                    {
                        text: this.$t('articleTable.seoScore') as string,
                        value: 'seoScore',
                        align: 'center',
                        sortable: false
                    },
                    {
                        text: this.$t('articleTable.edit') as string,
                        value: 'actions',
                        sortable: false,
                        align: 'center'
                    },
                    ...Object.values(this.positionSources).map(source => ({
                        text: this.$t(`articleTable.${source.field}.header`),
                        value: source.field,
                        align: 'center',
                        sortable: false
                    } as DataTableHeader)),
                    {
                        text: this.$t('articleTable.domainSeoScore.header.text') as string,
                        value: 'domainSeoScore',
                        align: 'center',
                        sortable: false
                    },
                    {
                        text: this.$t('articleTable.createdAt') as string,
                        value: 'created_at',
                        align: 'center'
                    },
                ];
            },
            articleCreationRoutes: function (): ArticleCreationRoute[] {
                return [
                    {
                        name: 'CreateArticle',
                        text: this.$t('article.create') as string,
                        description: this.$t('article.createDescription') as string
                    },
                    {
                        name: 'AnalyzeArticle',
                        text: this.$t('article.analyze.title') as string,
                        description: this.$t('article.analyze.description') as string
                    }
                ];
            }
        },
        watch: {
            options: {
                handler: function (newVal, oldVal) {
                    if (!this.initialLoad) {
                        if (newVal.itemsPerPage !== oldVal.itemsPerPage || newVal.sortBy !== oldVal.sortBy || newVal.sortDesc !== oldVal.sortDesc) {
                            // if sort or itemsPerPage changed, reset page to 1
                            this.getUserArticles();
                        } else if (newVal.page !== oldVal.page) {
                            if (newVal.page > oldVal.page) {
                                this.getUserArticles(true, this.articlesPage?.last);
                            } else {
                                this.getUserArticles(false, this.articlesPage?.first);
                            }
                        }
                    }
                },
                deep: true
            }
        },
        created: function () {
            this.getUserArticles();
        },
        methods: {
            onStartCardKey: function (event: KeyboardEvent, name: string): void {
                if (event.repeat || (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar')) {
                    return;
                }
                event.preventDefault();
                this.createArticle(name);
            },
            getUserArticles: function (nextPage = true, article?: DocumentData): void {
                this.loading = true;
                Firestore.getUserArticlesCount()
                    .then(count => {
                        this.serverItemsLength = count;
                        const sort = this.options.sortBy[0] ? {
                            field: this.options.sortBy[0],
                            sortString: this.options.sortDesc[0] ? SortString.DESC : SortString.ASC
                        } as Sort : undefined;
                        return nextPage ? Firestore.getUserArticlesNextPage(this.options.itemsPerPage, sort, article)
                            : Firestore.getUserArticlesPreviousPage(this.options.itemsPerPage, sort, article);
                    })
                    .then(articlesPage => {
                        this.articlesPage = articlesPage;
                        this.articles = articlesPage.items;
                    })
                    .finally(() => {
                        this.loading = false;
                        if (this.initialLoad) {
                            this.initialLoad = false;
                        }
                    });
            },
            createArticle: function (name: string): void {
                const intent = name === 'AnalyzeArticle' ? 'analyze' : 'generate';
                const decision = articleAccessFromStore(this.$store, intent);
                if (decision.action === 'wait') {
                    runWhenPlanLoaded(this, () => this.createArticle(name));
                    return;
                }
                if (decision.action === 'allow') {
                    this.$router.push({name: name});
                    return;
                }
                if (decision.reason === 'free-exhausted') {
                    this.$store.dispatch('toasts/add', {
                        type: 'error',
                        title: this.$t('subscriptions.freeAnalyses.exhausted.title') as string,
                        content: this.$t('subscriptions.freeAnalyses.exhausted.content', [this.maxFreeAnalyses]) as string,
                    } as Partial<Toast>);
                } else if (decision.reason === 'no-plan') {
                    this.$store.dispatch('toasts/add', {
                        type: 'error',
                        title: 'subscriptions.paywall.generateRequiresPlan.title',
                        content: 'subscriptions.paywall.generateRequiresPlan.content',
                    } as Partial<Toast>);
                } else {
                    this.$store.dispatch('toasts/add', {
                        type: 'error',
                        title: 'subscriptions.noInspirationsLeftThisPeriodUpgradeToContinue.title',
                        content: 'subscriptions.noInspirationsLeftThisPeriodUpgradeToContinue.content',
                    } as Partial<Toast>);
                }
                this.$router.push('/account');
            },

            articleKeyword: function (article: Article): string {
                return article.selectedKeyword === '' ? article.keyword : article.selectedKeyword;
            },
            articleEditIcon: function (article: Article): string {
                if (article.selectedKeyword) {
                    return 'description';
                } else if (article.language_code) {
                    return 'toc';
                } else {
                    return 'list_alt';
                }
            },
            articleEditLabel: function (article: Article): TranslateResult {
                if (article.selectedKeyword) {
                    return this.$t('steps.writeArticle');
                } else if (article.language_code) {
                    return this.$t('steps.selectKeyword');
                } else {
                    return this.$t('steps.initForm');
                }
            },
            openUrlDialog: function (index: number) {
                this.editingArticleIndex = index;
                this.urlDialogError = null;
                this.$nextTick(() => {
                    this.urlDialog = true;
                });
            },
            updateArticleUrl: function (url: string) {
                const index = this.editingArticleIndex as number;
                // new || deleted || changed
                if (this.articles[index].url === '' || url === '' || this.articles[index].url !== url) {
                    this.urlDialogLoading = true;
                    this.urlDialogError = null;
                    Firestore.updateUserArticleFields(this.articles[index], {
                        url: url,
                        organicPosition: null,
                        googleAIOPosition: null,
                        chatGPTPosition: null
                    } as Partial<Article>)
                        .then(() => {
                            this.urlDialog = false;
                            this.urlDialogLoading = false;
                            if (url !== '') {
                                (Object.keys(this.positionSources) as PositionSource[]).forEach(source => {
                                    this.getPosition(this.articles[index], source);
                                });
                            }
                            this.$store.dispatch('toasts/add', {
                                type: 'success',
                                title: 'article.updated',
                            } as Toast);
                        })
                        .catch(() => {
                            this.urlDialogLoading = false;
                            this.urlDialogError = this.$t('articleTable.url.dialog.errors.couldNotSave') as string;
                        });
                } else {
                    this.urlDialog = false;
                }
            },
            remeasurePosition: function (article: Article, sourceKey: PositionSource) {
                this.getPosition(article, sourceKey);
            },
            getPosition: function (article: Article, sourceKey: PositionSource) {
                if (!article.id) return;
                // update ui
                const source = this.positionSources[sourceKey];
                this.$set(article, source.field, {
                    position: null,
                    metadata: {},
                    error: null,
                    isProcessing: true,
                    timestamp: Date.now()
                });

                source.fetch(article.id)
                    .then(res => {
                        this.$set(article, source.field, res);
                    })
                    .catch(error => {
                        const raw = typeof error === 'string'
                            ? error
                            : (error && (error.message || error.reason)) || String(error);
                        window.console.warn('Position fetch failed', sourceKey, raw);
                        this.$set(article, source.field, {
                            position: null,
                            metadata: {},
                            error: 'articleTable.reloadableChip.errors.unexpectedError',
                            isProcessing: false,
                            timestamp: Date.now()
                        } as SearchPosition);
                    });
            },

            positionColor: function (value: number): string {
                return value < 11 ? 'accent' : value < 21 ? 'warning' : 'error';
            },
            seoScoreColor: function (value: number): string {
                return value > 66 ? 'accent' : value > 33 ? 'warning' : 'error';
            },

            openDomainSeoScore: function (article: Article) {
                const url = new URL(article.url);
                window.open(ExternalLinks.domainSeoTest + url.hostname, '_blank');
            },
            hasCreditForRoute: function (routeName: string): boolean {
                if (routeName === 'AnalyzeArticle') {
                    return articleAccessFromStore(this.$store, 'analyze').action !== 'paywall';
                } else if (routeName === 'CreateArticle') {
                    return articleAccessFromStore(this.$store, 'generate').action !== 'paywall';
                }
                return false;
            },
            headerSlotName: function (name: string) {
                return 'header.' + name;
            },
            itemSlotName: function (name: string) {
                return 'item.' + name;
            },
        }
    });

// TODO: if order on selectedKeyword (does not work as user would expect) - firestore uses lexicographical order
// - would need another field (on create - copy keyword / on update - copy selectedKeyword to keyword)
// - existing articles would need to be updated
// - lexicographical order (asc: V > i > v)
</script>

<style scoped lang="sass">
.home-start-card
  flex: 1
  width: 100%
  min-width: 24.75em
  padding: 0
  border: 0
  background: transparent
  font: inherit
  letter-spacing: normal
  text-align: left
  text-transform: none
  appearance: none
  cursor: pointer

  &:focus
    outline: 3px solid var(--sw-primary-fill)
    outline-offset: 2px

  ::v-deep .v-card.accent.theme--dark,
  ::v-deep .v-card.warning.theme--dark
    color: #ffffff !important

    .v-icon
      color: #ffffff !important

  &:hover ::v-deep .v-card--hover
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2)

// Beat App.vue filled-chip white text; SEO score + organic position chips on green.
::v-deep .v-data-table
  .v-chip.accent:not(.v-chip--outlined),
  .v-chip.success:not(.v-chip--outlined)
    color: #ffffff !important

    .v-chip__content
      color: #ffffff !important
</style>
